import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronRight, Landmark, Layers3 } from "lucide-react";
import { SECURITY_ROLE_LABELS, CHARGE_MODE_LABELS, DISTRIBUTION_RIGHTS_LABELS, PublicChargeRegistry } from "../../../api/charges";
import OverviewModal from "./OverviewModal";
import AddChargeModal from "./AddChargeModal";
import ManageChargeModal from "./ManageChargeModal";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import { selectAllCharges, selectChargesError, selectChargesStatus } from "../Logic/chargesSlice";
import { fetchCharges } from "../Logic/chargesThunk";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";

interface ChargesOverviewModalProps {
  onClose: () => void;
}

const ChargesOverviewModal: React.FC<ChargesOverviewModalProps> = ({ onClose }) => {
  const [showAddChargeModal, setShowAddChargeModal] = useState(false);
  const [showManageChargeModal, setShowManageChargeModal] = useState(false);
  const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);

  const charges = useAppSelector(selectAllCharges);
  const status = useAppSelector(selectChargesStatus);
  const fetchError = useAppSelector(selectChargesError);
  const isLoading = status === "loading";

  const dispatch = useAppDispatch();
  const { id: case_id } = useParams<{ id: string }>();

  const handleRetry = () => case_id && dispatch(fetchCharges(case_id));

  const handleAddCharge = () => setShowAddChargeModal(true);

  const handleManageCharge = (chargeId: string) => {
    setSelectedChargeId(chargeId);
    setShowManageChargeModal(true);
  };

  if (showManageChargeModal && selectedChargeId) {
    return (
      <ManageChargeModal
        chargeId={selectedChargeId}
        onBack={() => setShowManageChargeModal(false)}
        onClose={onClose}
      />
    );
  }

  if (showAddChargeModal) {
    return (
      <AddChargeModal
        onClose={() => { setShowAddChargeModal(false); onClose(); }}
        onBack={() => { setShowAddChargeModal(false); onClose(); }}
      />
    );
  }

  return (
    <OverviewModal
      title="Charges & Securities"
      addButtonLabel="Add New Charge"
      onAction={handleAddCharge}
      onClose={onClose}
    >
      {isLoading ? (
        <CustomLoadingSpinner
          title="Loading Charges..."
          subtitle="Fetching charges associated with this case."
          minHeight="min-h-[160px]"
        />
      ) : fetchError ? (
        <ErrorDisplay
          error="Unable to load charges. Please try again."
          onRetry={handleRetry}
        />
      ) : charges.length === 0 ? 
      (
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-3">
          <Layers3 size={40} className="text-[#A3A3A3]" />

          <div className="text-sm font-medium text-[#0A0A0A]">No charges added</div>
          <div className="text-xs text-[#737373]">Add your first charge to continue.</div>

        </div>
      ) : (
        <div className="pr-1">
          {charges.map(({ charges, asset_linkages, loan_linkages }) => {
            const registryIdentifier = charges.public_registry === PublicChargeRegistry.CERSAI
              ? charges.registry_identifiers?.security_interest_id
              : "";

            return (
              <div key={charges.charge_id} className="flex items-center justify-between py-2">
                <div className="flex flex-1 flex-col gap-1.5">

                  <div className="max-w-[450px] truncate text-[14px] font-normal leading-5 text-[#0A0A0A]">
                    {SECURITY_ROLE_LABELS[charges.security_role!]}
                    {registryIdentifier && ` | ${registryIdentifier}`}
                  </div>

                  <div className="text-[12px] font-normal leading-4 text-[#737373]">
                    {CHARGE_MODE_LABELS[charges.charge_mode!]} 
                    • {DISTRIBUTION_RIGHTS_LABELS[charges.distribution_rights!]}
                    • {charges.public_registry}
                  </div>

                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded-[8px] bg-[#F3F4F6] px-2 py-1">
                      <Layers3 size={16} className="text-[#3B82F6]" />
                      <span className="text-[12px] font-medium text-[#4B5563]">
                        {asset_linkages.length} Asset{asset_linkages.length !== 1 && "s"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 rounded-[8px] bg-[#F3F4F6] px-2 py-1">
                      <Landmark size={16} className="text-[#22C55E]" />
                      <span className="text-[12px] font-medium text-[#4B5563]">
                        {loan_linkages.length} Loan{loan_linkages.length !== 1 && "s"}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleManageCharge(charges.charge_id)}
                  className="flex items-center gap-0.5 self-center text-[12px] font-medium text-[#3551F3]"
                >
                  Manage
                  <ChevronRight size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </OverviewModal>
  );
};

export default ChargesOverviewModal;